import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Search,
  Plus,
  ChevronLeft,
  ChevronRight,
  Edit,
  Trash2,
  CheckCircle,
  XCircle,
  Baby,
  Heart,
  Users,
  AlertTriangle,
} from 'lucide-react';
import { getSupabaseAdminClient, BabyName } from '@parentpath/supabase-client';
import toast from 'react-hot-toast';
import { LocaleSelector } from '@/components/LocaleSelector';
import { useAdminLocale } from '@/hooks/useAdminLocale';
import { BabyNameForm, BabyNameFormValues } from './BabyNameForm';

const supabase = getSupabaseAdminClient();

type GenderFilter = 'all' | 'male' | 'female' | 'neutral';
type StatusFilter = 'all' | 'active' | 'inactive';

export function NamesPage() {
  const { contentLocale } = useAdminLocale();
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState('');
  const [genderFilter, setGenderFilter] = useState<GenderFilter>('all');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [currentPage, setCurrentPage] = useState(1);
  // CRUD-Modals: Anlegen (bool), Bearbeiten / Löschen (jeweils die betroffene Zeile).
  const [isCreating, setIsCreating] = useState(false);
  const [editingName, setEditingName] = useState<BabyName | null>(null);
  const [deletingName, setDeletingName] = useState<BabyName | null>(null);
  const pageSize = 15;

  // Fetch names with pagination
  const { data: namesData, isLoading } = useQuery({
    queryKey: ['names', contentLocale, currentPage, searchQuery, genderFilter, statusFilter],
    queryFn: async () => {
      let query = supabase
        .from('baby_names')
        .select('*', { count: 'exact' })
        .eq('locale', contentLocale);

      // Apply search filter
      if (searchQuery) {
        query = query.ilike('name', `%${searchQuery}%`);
      }

      // Apply gender filter
      if (genderFilter !== 'all') {
        query = query.eq('gender', genderFilter);
      }

      // Apply status filter
      if (statusFilter === 'active') {
        query = query.eq('is_active', true);
      } else if (statusFilter === 'inactive') {
        query = query.eq('is_active', false);
      }

      // Apply pagination
      const from = (currentPage - 1) * pageSize;
      const to = from + pageSize - 1;

      const { data, count, error } = await query
        .order('name', { ascending: true })
        .range(from, to);

      if (error) throw error;

      return {
        names: data as BabyName[],
        totalCount: count || 0,
      };
    },
  });

  // Fetch stats
  const { data: stats } = useQuery({
    queryKey: ['names-stats', contentLocale],
    queryFn: async () => {
      const [totalResult, activeResult, maleResult, femaleResult] = await Promise.all([
        supabase.from('baby_names').select('id', { count: 'exact', head: true }).eq('locale', contentLocale),
        supabase.from('baby_names').select('id', { count: 'exact', head: true }).eq('locale', contentLocale).eq('is_active', true),
        supabase.from('baby_names').select('id', { count: 'exact', head: true }).eq('locale', contentLocale).eq('gender', 'male'),
        supabase.from('baby_names').select('id', { count: 'exact', head: true }).eq('locale', contentLocale).eq('gender', 'female'),
      ]);

      return {
        total: totalResult.count || 0,
        active: activeResult.count || 0,
        male: maleResult.count || 0,
        female: femaleResult.count || 0,
      };
    },
  });

  // Erfolg/Fehler-Behandlung ist für Create & Update identisch: Listen + Stats
  // auffrischen, Modal schließen, Duplikate (Unique name+gender, 23505) freundlich melden.
  const invalidateNames = () => {
    queryClient.invalidateQueries({ queryKey: ['names'] });
    queryClient.invalidateQueries({ queryKey: ['names-stats'] });
  };

  const handleWriteError = (error: unknown) => {
    if ((error as { code?: string })?.code === '23505') {
      toast.error('Name existiert bereits in der Datenbank');
      return;
    }
    toast.error('Fehler beim Speichern des Namens');
  };

  // `as any` auf den Query-Builder ist der projektweite Workaround für die
  // fehlerhaften Supabase-Generics (vgl. ManualProductsPage); `values`/`id` sind typisiert.
  const createMutation = useMutation({
    mutationFn: async (values: BabyNameFormValues) => {
      const { error } = await (supabase.from('baby_names') as any).insert(values);
      if (error) throw error;
    },
    onSuccess: () => {
      invalidateNames();
      setIsCreating(false);
      toast.success('Name wurde hinzugefügt');
    },
    onError: handleWriteError,
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, values }: { id: string; values: BabyNameFormValues }) => {
      const { error } = await (supabase.from('baby_names') as any).update(values).eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      invalidateNames();
      setEditingName(null);
      toast.success('Name wurde aktualisiert');
    },
    onError: handleWriteError,
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('baby_names').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      invalidateNames();
      setDeletingName(null);
      toast.success('Name wurde gelöscht');
    },
    onError: () => {
      toast.error('Fehler beim Löschen des Namens');
    },
  });

  const totalPages = Math.ceil((namesData?.totalCount || 0) / pageSize);

  const getGenderBadge = (gender: string) => {
    switch (gender) {
      case 'male':
        return (
          <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full">
            Männlich
          </span>
        );
      case 'female':
        return (
          <span className="px-2 py-1 bg-rose-100 text-rose-700 text-xs rounded-full">
            Weiblich
          </span>
        );
      case 'neutral':
        return (
          <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded-full">
            Neutral
          </span>
        );
      default:
        return null;
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
  };

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-2">
          <h1 className="text-3xl font-bold text-slate-900">Namen-Datenbank</h1>
          <LocaleSelector />
        </div>
        <p className="text-slate-600">Alle verfügbaren Baby-Namen verwalten</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow-sm p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center">
              <Baby className="w-5 h-5 text-teal-600" />
            </div>
            <div>
              <div className="text-xl font-bold text-slate-900">{stats?.total || 0}</div>
              <div className="text-sm text-slate-600">Gesamt</div>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <div className="text-xl font-bold text-slate-900">{stats?.active || 0}</div>
              <div className="text-sm text-slate-600">Aktiv</div>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Users className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <div className="text-xl font-bold text-slate-900">{stats?.male || 0}</div>
              <div className="text-sm text-slate-600">Männlich</div>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-rose-100 rounded-lg flex items-center justify-center">
              <Heart className="w-5 h-5 text-rose-600" />
            </div>
            <div>
              <div className="text-xl font-bold text-slate-900">{stats?.female || 0}</div>
              <div className="text-sm text-slate-600">Weiblich</div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters & Search */}
      <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
        <div className="flex flex-wrap gap-4 items-center">
          <form onSubmit={handleSearch} className="flex-1 min-w-[300px]">
            <div className="relative">
              <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Nach Name suchen..."
                className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none"
              />
            </div>
          </form>
          <div className="flex gap-2">
            <select
              value={genderFilter}
              onChange={(e) => {
                setGenderFilter(e.target.value as GenderFilter);
                setCurrentPage(1);
              }}
              className="px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none"
            >
              <option value="all">Alle Geschlechter</option>
              <option value="male">Männlich</option>
              <option value="female">Weiblich</option>
              <option value="neutral">Neutral</option>
            </select>
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value as StatusFilter);
                setCurrentPage(1);
              }}
              className="px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none"
            >
              <option value="all">Alle Status</option>
              <option value="active">Aktiv</option>
              <option value="inactive">Inaktiv</option>
            </select>
            <button
              onClick={() => setIsCreating(true)}
              className="px-4 py-2 bg-teal-500 hover:bg-teal-600 text-white rounded-lg font-medium flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Name hinzufügen
            </button>
          </div>
        </div>
      </div>

      {/* Names Table */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-100">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Name</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Geschlecht</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Herkunft</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Bedeutung</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Aktionen</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {isLoading ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-slate-500">
                    Lädt...
                  </td>
                </tr>
              ) : namesData?.names?.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-slate-500">
                    Keine Namen gefunden
                  </td>
                </tr>
              ) : (
                namesData?.names?.map((name) => (
                  <tr key={name.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3">
                      <span className="text-sm font-medium text-slate-900">{name.name}</span>
                    </td>
                    <td className="px-4 py-3">
                      {getGenderBadge(name.gender)}
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-slate-600">{name.origin || '-'}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-slate-600 truncate max-w-xs block">
                        {name.meaning || '-'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {name.is_active ? (
                        <span className="flex items-center gap-1 text-emerald-600 text-sm">
                          <CheckCircle className="w-4 h-4" />
                          Aktiv
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 text-slate-400 text-sm">
                          <XCircle className="w-4 h-4" />
                          Inaktiv
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => setEditingName(name)}
                          className="p-1 hover:bg-slate-100 rounded"
                          title="Bearbeiten"
                        >
                          <Edit className="w-4 h-4 text-slate-500" />
                        </button>
                        <button
                          onClick={() => setDeletingName(name)}
                          className="p-1 hover:bg-slate-100 rounded"
                          title="Löschen"
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="px-4 py-3 bg-slate-50 border-t border-slate-200 flex items-center justify-between">
          <div className="text-sm text-slate-600">
            Zeige {((currentPage - 1) * pageSize) + 1}-{Math.min(currentPage * pageSize, namesData?.totalCount || 0)} von {namesData?.totalCount?.toLocaleString('de-DE') || 0}
          </div>
          <div className="flex gap-1">
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="px-3 py-1 border border-slate-300 rounded hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            {Array.from({ length: Math.min(5, totalPages || 1) }, (_, i) => i + 1).map((page) => (
              <button
                key={page}
                onClick={() => setCurrentPage(page)}
                className={`px-3 py-1 border border-slate-300 rounded ${
                  currentPage === page
                    ? 'bg-teal-500 text-white border-teal-500'
                    : 'hover:bg-slate-100'
                }`}
              >
                {page}
              </button>
            ))}
            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages || 1, p + 1))}
              disabled={currentPage === totalPages}
              className="px-3 py-1 border border-slate-300 rounded hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Anlegen: leeres Formular, locale auf die gerade angezeigte Sprache vorbelegt. */}
      {isCreating && (
        <BabyNameForm
          title="Name hinzufügen"
          submitLabel="Hinzufügen"
          isLoading={createMutation.isPending}
          initialValues={{ locale: contentLocale }}
          onSubmit={(values) => createMutation.mutate(values)}
          onCancel={() => setIsCreating(false)}
        />
      )}

      {/* Bearbeiten: Formular aus der Zeile vorbefüllt, Update per id. */}
      {editingName && (
        <BabyNameForm
          title="Name bearbeiten"
          submitLabel="Speichern"
          isLoading={updateMutation.isPending}
          initialValues={editingName}
          onSubmit={(values) => updateMutation.mutate({ id: editingName.id, values })}
          onCancel={() => setEditingName(null)}
        />
      )}

      {/* Löschen: Bestätigungs-Dialog im Overlay-Stil (kein window.confirm). */}
      {deletingName && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" style={{ backdropFilter: 'blur(2px)' }}>
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-slate-900">Name löschen</h2>
                <p className="text-sm text-slate-600 mt-1">
                  Soll <span className="font-medium text-slate-900">{deletingName.name}</span>{' '}
                  wirklich dauerhaft gelöscht werden? Dies kann nicht rückgängig gemacht werden.
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeletingName(null)}
                className="px-4 py-2 border border-slate-300 rounded-lg text-slate-700 hover:bg-slate-50 font-medium"
              >
                Abbrechen
              </button>
              <button
                onClick={() => deleteMutation.mutate(deletingName.id)}
                disabled={deleteMutation.isPending}
                className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Löschen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
